/**
 * apps/command/src/realtime/useFleet.ts — the fleet, live.
 *
 * ============================================================================
 * HTTP IS THE SOURCE. THE SOCKET IS AN ACCELERATOR.
 * ============================================================================
 *
 * §14's failure behaviour is a requirement, not a fallback story: "if
 * `realtime` is down, everything degrades to HTTP polling… the fleet must never
 * become uncontrollable because one process died."
 *
 * So this hook ALWAYS fetches `GET /mbt/fleet/kiosks` and always keeps polling
 * it. The socket, when it connects, merges fresher rows on top. Kill
 * `services/realtime` and this screen keeps working at poll cadence with no
 * error state and no empty grid — which is the behaviour to verify, so the poll
 * is unconditional rather than "started if the socket fails".
 *
 * ============================================================================
 * §15.9 — "LIVE WITHOUT REFRESH", AND §3.5 — NEVER FAKE-LIVE.
 * ============================================================================
 *
 * There is no refresh button anywhere in this panel. Every tile ticks by
 * itself, and every tile carries the moment its data arrived. Two clocks are
 * kept apart on purpose and the UI shows both:
 *
 *   `telemetry_at` — when the KIOSK reported. This is the "as of" §3.5 requires,
 *                    and it is what `deriveKioskStatus` reads.
 *   `fetchedAt`    — when WE last succeeded in asking. If the API itself goes
 *                    away, the tiles are still true as of this moment and the
 *                    panel must say so rather than showing confident numbers
 *                    from a server it can no longer reach.
 *
 * `now` ticks once a second so a tile ages on screen without anyone touching
 * it: a kiosk that stops reporting drifts «Sogʻlom» → «Nosoz» → «Oflayn» by
 * itself, because status is derived from the clock rather than pushed by an
 * event that might never come.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deriveKioskStatus, type KioskStatus } from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { api, ApiError, realtimeUrl, unwrap } from '../api/client.js';

export type MbtKiosk = components['schemas']['MbtKiosk'];

/** How often the HTTP source is re-read. Half the kiosks' own 30 s cadence, so a
 *  report is never more than one poll behind even with the socket down. */
const POLL_MS = 15_000;

/** §14's kiosk backoff shape, applied to the panel's own socket: 1 s → 30 s. */
const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export interface FleetState {
  kiosks: MbtKiosk[];
  /** True when `mahalla_scope` narrowed the list — the panel must say so. */
  scoped: boolean;
  /** When the HTTP source last answered. Null until the first success. */
  fetchedAt: Date | null;
  /** Whether the live socket is currently attached. Never conflated with status. */
  live: boolean;
  loading: boolean;
  error: 'forbidden' | 'network' | null;
  /** Ticks every second so derived status ages without a refresh. */
  now: Date;
  reload: () => void;
}

/**
 * §4.3, computed here so a tile can age between polls.
 *
 * IT MUST REACH THE SAME ANSWER THE SERVER JUST DID. `deriveKioskStatus` is
 * shared, but it only agrees if it is given the same inputs — which is why the
 * tile carries `open_alerts_critical` and not merely a total. An earlier
 * version passed no alert counts at all, and the result was a kiosk the server
 * called «Diqqat» rendering as «Sogʻlom» in the grid directly beneath the alert
 * that said otherwise.
 */
export function statusOf(kiosk: MbtKiosk, now: Date): KioskStatus {
  const critical = kiosk.open_alerts_critical ?? 0;
  const total = kiosk.open_alerts ?? 0;
  return deriveKioskStatus({
    telemetryAt: kiosk.telemetry_at ? new Date(kiosk.telemetry_at) : null,
    maintenanceUntil: kiosk.maintenance_until ? new Date(kiosk.maintenance_until) : null,
    hasCriticalAlert: critical > 0,
    hasWarningAlert: total - critical > 0,
    now,
  });
}

export function useFleet(onExpired: () => void): FleetState {
  const [kiosks, setKiosks] = useState<MbtKiosk[]>([]);
  const [scoped, setScoped] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'forbidden' | 'network' | null>(null);
  const [live, setLive] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  // -- the clock -------------------------------------------------------------
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(timer);
  }, []);

  // -- the source: HTTP, always ---------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const data = unwrap(await api().GET('/mbt/fleet/kiosks'));
        if (cancelled) return;
        setKiosks(data.items as MbtKiosk[]);
        setScoped(data.scoped);
        setFetchedAt(new Date());
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 401) {
          onExpired();
          return;
        }
        if (caught instanceof ApiError && caught.status === 403) {
          setError('forbidden');
        } else {
          // The tiles already on screen are NOT cleared. They remain true as of
          // `fetchedAt`, which the header shows — §3.5 again: last-known state
          // with its timestamp beats an empty grid that implies a dead city.
          setError('network');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [onExpired, reloadToken]);

  // -- the accelerator: the operator socket ---------------------------------
  const socketRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    let closed = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const connect = (): void => {
      if (closed) return;
      const socket = new WebSocket(realtimeUrl('/realtime/operator'));
      socketRef.current = socket;

      socket.onopen = () => {
        attempt = 0;
        setLive(true);
      };

      socket.onmessage = (event) => {
        let frame: { type?: string; [key: string]: unknown };
        try {
          frame = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (frame.type !== 'kiosk') return;
        const update = frame as unknown as MbtKiosk & { device_id: string };
        setKiosks((current) => {
          const index = current.findIndex((kiosk) => kiosk.id === update.device_id);
          if (index === -1) {
            // A kiosk the poll has not seen yet — a device enrolled since the
            // last fetch. Ask HTTP for the full row rather than synthesising a
            // partial tile from a frame that carries no label or mahalla name.
            reload();
            return current;
          }
          const existing = current[index];
          if (!existing) return current;
          const next = [...current];
          next[index] = {
            ...existing,
            // §3.5 — the pair is taken together or not at all. A frame without
            // both leaves the previous pair intact rather than half-updating
            // the tile.
            ...(update.telemetry !== undefined && update.telemetry_at !== undefined
              ? { telemetry: update.telemetry, telemetry_at: update.telemetry_at }
              : {}),
            // Alert counts are NOT on the socket frame — the realtime service
            // reports telemetry and presence, not the queue. Merging a frame
            // must therefore PRESERVE whatever the last poll said rather than
            // spreading `undefined` over it, or every live update would silently
            // clear the counts and a critical kiosk would flip to «Sogʻlom» for
            // up to 15 seconds. Keeping the existing fields is the whole point
            // of merging into `existing` rather than replacing it.
          };
          return next;
        });
      };

      socket.onclose = (event) => {
        setLive(false);
        socketRef.current = null;
        if (closed) return;
        // 4001/4003 are decisions, not weather. Retrying a refused credential on
        // a one-second timer is how a panel left open overnight turns into a
        // denial-of-service against its own server.
        if (event.code === 4001 || event.code === 4003) return;
        attempt += 1;
        const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** (attempt - 1));
        // Jitter, so 35 panels (or one panel and 35 kiosks) do not all return
        // at the same instant after a restart.
        timer = setTimeout(connect, backoff * (0.5 + Math.random() / 2));
      };

      socket.onerror = () => {
        // Handled by `onclose`, which always follows. Swallowed here so a
        // realtime process that is simply not running does not fill an
        // operator's console with errors about an optional accelerator.
      };
    };

    connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [reload]);

  return useMemo(
    () => ({ kiosks, scoped, fetchedAt, live, loading, error, now, reload }),
    [kiosks, scoped, fetchedAt, live, loading, error, now, reload],
  );
}
